import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import type { Mailbox } from '@bekebi/contracts/api';
import { Button } from '../../components/button';
import { useHasPermission } from '../../session/current-user';
import { useChangeMailboxQuota, useMailboxes } from './api';
import { MailServerNotReady, mailboxErrorMessage } from './mailbox-shared';
import {
  bytesToGbText,
  canChangeSize,
  formatBytes,
  formatQuota,
  gbTextToBytes,
  quotaHint,
  quotaProblem,
} from './quota';

/**
 * Change a mailbox's size (/app/mailboxes/:address/size, `mail.mailboxes.manage`) —
 * PATCH /api/mail/mailboxes/:address with the new size only (the approved edit; OD-1). The
 * mailbox is taken from the Mail Server's current list, and the change is reported only after the
 * API confirms that the Mail Server applied it. ICT is offered no form for a mailbox above 5 GB.
 */
export function MailboxSizePage() {
  const { address = '' } = useParams();
  const { state } = useMailboxes();
  const mailbox =
    state.kind === 'connected'
      ? (state.mailboxes.find((m) => m.address === address.toLowerCase()) ?? null)
      : null;

  return (
    <div className="max-w-xl">
      <p className="text-sm">
        <Link
          to="/app/mailboxes"
          className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
        >
          Mailboxes
        </Link>
      </p>
      <h1 className="mt-2">Change mailbox size</h1>
      <p className="mt-1 break-all text-sm text-muted">{address}</p>
      <div className="mt-6">
        {state.kind !== 'connected' ? (
          <MailServerNotReady state={state} />
        ) : mailbox ? (
          <SizeForm mailbox={mailbox} />
        ) : (
          <p role="alert" className="rounded-md bg-danger-soft p-3 text-sm text-fg">
            The Mail Server reports no mailbox with this address.
          </p>
        )}
      </div>
    </div>
  );
}

function SizeForm({ mailbox }: { mailbox: Mailbox }) {
  const unlimited = useHasPermission('mail.mailboxes.manage_unlimited');
  const change = useChangeMailboxQuota();
  const navigate = useNavigate();

  const schema = z.object({
    size: z.string().superRefine((value, ctx) => {
      const problem = quotaProblem(gbTextToBytes(value), unlimited);
      if (problem) ctx.addIssue({ code: 'custom', message: problem });
    }),
  });
  type Fields = z.infer<typeof schema>;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Fields>({
    resolver: zodResolver(schema),
    defaultValues: { size: mailbox.quotaBytes === null ? '' : bytesToGbText(mailbox.quotaBytes) },
  });

  const current = (
    <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 rounded-lg border border-line bg-surface p-4 text-sm">
      <dt className="text-muted">Current size</dt>
      <dd className="text-fg">{formatQuota(mailbox.quotaBytes)}</dd>
      <dt className="text-muted">Space used</dt>
      <dd className="text-fg">
        {mailbox.usedBytes === null ? 'Not reported' : formatBytes(mailbox.usedBytes)}
      </dd>
    </dl>
  );

  if (!canChangeSize(mailbox.quotaBytes, unlimited)) {
    return (
      <div className="space-y-4">
        {current}
        <p className="rounded-md bg-raised p-3 text-sm text-fg-2">
          {mailbox.quotaBytes === null
            ? 'This mailbox has no size limit and is managed by the Admin.'
            : 'This mailbox is above 5 GB and is managed by the Admin.'}
        </p>
      </div>
    );
  }

  const onSubmit = handleSubmit((values) =>
    change.mutate(
      { address: mailbox.address, quotaBytes: gbTextToBytes(values.size)! },
      {
        onSuccess: (updated) =>
          void navigate('/app/mailboxes', {
            state: {
              notice: `${updated.address} now has ${formatQuota(updated.quotaBytes)}.`,
            },
          }),
      },
    ),
  );

  return (
    <div className="space-y-5">
      {current}
      {change.isError ? (
        <p
          role="alert"
          className="rounded-lg border border-danger-line bg-danger-soft px-4 py-3 text-sm text-fg"
        >
          {mailboxErrorMessage(change.error)} The size was not changed.
        </p>
      ) : null}
      <form onSubmit={onSubmit} noValidate aria-label="Change mailbox size" className="space-y-5">
        <div>
          <label htmlFor="size" className="block text-sm font-medium text-fg">
            New size (GB)
          </label>
          <input
            id="size"
            type="number"
            inputMode="decimal"
            min={1}
            step={0.1}
            {...(unlimited ? {} : { max: 5 })}
            aria-invalid={errors.size ? 'true' : 'false'}
            aria-describedby={`size-hint${errors.size ? ' size-error' : ''}`}
            className="mt-1 block h-11 w-full max-w-[10rem] rounded-md border border-line-strong bg-surface px-3 text-sm"
            {...register('size')}
          />
          <p id="size-hint" className="mt-1.5 text-xs text-muted">
            {quotaHint(unlimited)}
          </p>
          {errors.size ? (
            <p id="size-error" className="mt-1.5 text-sm text-danger">
              {errors.size.message}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-3">
          <Button type="submit" disabled={change.isPending}>
            {change.isPending ? 'Saving…' : 'Save size'}
          </Button>
          <Link to="/app/mailboxes" className="inline-flex min-h-10 items-center px-2 text-sm">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
